/**
 * Phase A.2 — friendly tool labels for the chat UI.
 *
 * The agent's tool names are technical and noisy in chat ("apply_workbook_plan",
 * "query_snapshot"). Users see short verb-phrases instead. Backend logs keep
 * the technical names — this is purely a presentation layer.
 *
 * `toolLabel(name)` returns a friendly label for known tools and falls back to
 * the technical name for anything unmapped. Adding a tool to the dispatcher
 * does NOT require updating this file; the technical name is the safe default.
 *
 * execute_office_js has its own UI (see ToolCallChip in App.tsx) which uses
 * the agent-supplied `description` as the label, so it doesn't need an entry
 * here — but we provide one as a streaming-state fallback before the input
 * arrives.
 */

const LABELS: Record<string, string> = {
  // ---- Orientation / reads ----
  get_workbook_info:        'Checking workbook',
  list_sheets:              'Listing sheets',
  read_selection:           'Reading selection',
  read_range:               'Reading range',
  read_used_range:          'Reading sheet',
  read_formulas:            'Reading formulas',
  read_cell_types:          'Reading cell types',
  read_workbook_structure:  'Reading workbook structure',
  list_named_ranges:        'Listing named ranges',
  list_tables:              'Listing tables',
  read_table:               'Reading table',

  // ---- Snapshot / ingested-mode reads ----
  snapshot_sheet:           'Reading sheet',
  describe_snapshot:        'Inspecting structure',
  compute_on_snapshot:      'Computing',
  scan_for_dashboard:       'Scanning for dashboard',
  query_snapshot:           'Reading workbook',
  get_domain_reference:     'Looking up reference',

  // ---- Atomic writes ----
  apply_workbook_plan:      'Editing workbook',
  build_scaffold:           'Building dashboard',
  write_from_snapshot:      'Writing data',
  execute_office_js:        'Working',  // streaming-state fallback only; ToolCallChip overrides with the agent's description

  // ---- Direct writes / structural ops ----
  create_sheet:             'Creating sheet',
  write_sheet:              'Writing sheet',
  add_formula:              'Adding formula',
  add_formulas:             'Adding formulas',
  add_formulas_bulk_template: 'Adding formulas',
  insert_rows:              'Inserting rows',
  insert_columns:           'Inserting columns',
  delete_rows:              'Deleting rows',
  delete_columns:           'Deleting columns',
  clear_range:              'Clearing cells',
  copy_range:               'Copying cells',
  find_replace:             'Find and replace',
  sort_range:               'Sorting',
  remove_duplicates:        'Removing duplicates',

  // ---- Formatting ----
  format_range:             'Formatting',
  apply_section_format:     'Formatting',
  freeze_panes:             'Freezing panes',
  set_column_widths:        'Setting column widths',
  conditional_format:       'Adding highlights',
  apply_conditional_formats_bulk: 'Adding highlights',
  add_chart:                'Adding chart',
  add_charts_bulk:          'Adding charts',
  add_data_validation:      'Adding validation',

  // ---- Workbook / sheet management ----
  set_calculation_mode:     'Setting calculation mode',
  set_sheet_visibility:     'Setting sheet visibility',
  set_sheet_tab_color:      'Colouring tab',
  protect_sheet:            'Protecting sheet',
  unprotect_sheet:          'Unprotecting sheet',
  mark_input_cells:         'Marking input cells',
  hide_rows_columns:        'Hiding rows/columns',
  add_comment:              'Adding comment',
  define_named_range:       'Defining named range',
  create_table:             'Creating table',

  // ---- Compliance ----
  list_standards:           'Listing standards',
  detect_standard:          'Detecting standard',
  validate_standard:        'Validating standard',
  generate_disclosure_checklist: 'Generating disclosure checklist',

  // ---- Document export ----
  export_to_pdf:            'Generating PDF',
  generate_word_memo:       'Generating Word document',
  generate_powerpoint_deck: 'Generating PowerPoint',

  // ---- Financial math ----
  calculate_landed_cost:    'Calculating landed cost',
  apply_cpi_escalation:     'Calculating CPI escalation',
  nominal_to_real:          'Converting to real values',
  bond_value:               'Pricing bond',
  yield_to_maturity:        'Solving yield to maturity',
  test_covenants:           'Testing covenants',
  investment_analysis:      'Running investment analysis',
};

export function toolLabel(name: string): string {
  return LABELS[name] ?? name;
}
